// import './types.js';
// ── STATE ─────────────────────────────────────────────────────────
/** @type {import('./types.ts').AppState} */
const ST = {
  screen: 'splash',
  prev: 'brief',
  apiKey: localStorage.getItem('sf_key') || '',
  notificationEmail: localStorage.getItem('sf_notify_email') || '',
  cfToken: localStorage.getItem('sf_cf_token') || '',
  cfAccountId: localStorage.getItem('sf_cf_account') || '',
  cfProject: localStorage.getItem('sf_cf_project') || '',
  pendingFn: null,
  brief: {
    businessDesc: '',
    customerProblem: '',
    desiredAction: '',
    actionDetail: '',
    leadMagnet: '',
    vibes: [],
    refsite: '',
  },
  briefStep: 0,
  followUps: [],
  enrichment: {},
  logoImg: null,
  logoUrl: '',
  inspoImg: null,
  concept: null,
  conceptContext: '',
  chatImage: null, // { data, type, name, preview, mode: 'match'|'use' }
  chatTab: 'sections', // 'sections' | 'chat'
  designTab: 'brand', // 'brand' | 'chat'
  chatTargetSection: null, // section ID from click-to-select
  inlineEditMode: false, // toggle for inline text editing in preview
  stockImages: [],
  pages: [
    {
      id: 'page-home',
      name: 'Home',
      sects: [],
      shellHtml: '',
      shellStatus: 'pending',
      assembled: '',
      editHist: [],
      editUndoStack: [],
      chatLog: [],
    },
  ],
  activePage: 0,
  expanded: null,
  running: false,
  editInput: '',
  isEditing: false,
  device: 'desktop',
  adminTab: 'generation',
  adminId: null,
  adminEditing: false,
  adminDraft: '',
  adminSaved: false,
};

// ── FLOW STATE ─────────────────────────────────────────────────────
// Extended state for new onboarding paths
Object.assign(ST, {
  extractedFacts: null,
  factsConfirmed: false,
  editingFact: null,
  purchasedFont: '',
  brandGuideImg: null,
  startPath: null, // 'questions' | 'docs' | 'existing'
  uploads: {
    docs: [], // [{name, ext, markdown, status:'processing'|'done'|'error'}]
    images: [], // [{name, type, data, preview, analysis:null, analysisStatus:'pending'|'analyzing'|'done'|'error'}]
  },
  existingUrl: '',
  existingOpt: 'keep', // 'keep' | 'refresh' | 'rebrand'
  existingScrape: null, // scrape result {url, title, metaDescription, ogImage, rawHtml}
  copywritingBrief: null, // Option 2 (refresh) intermediate Claude call result
  docNotes: '',
  activeDocIdx: 0,
  imgAnalysisTimer: null, // debounce timer for auto-analysis
  dragSrcIdx: null,
  pickerOpen: false,
  marketingBrief: null, // Unified brief data from AI analysis (all 3 paths)
  discoveredPages: [], // DiscoveredPage[] — from sitemap/nav discovery
  homePageScreenshot: null, // UploadedImage[] | null — cached home page screenshot for page planner
});

// ── PAGE HELPERS ──────────────────────────────────────────────────
/** @returns {import('./types.ts').Page} */
function curPage() {
  return ST.pages[ST.activePage] || ST.pages[0];
}

/**
 * Generate clean section ID, deduping within a sections array
 * @param {string} type
 * @param {import('./types.ts').Section[]} sects
 * @returns {string}
 */
function sectionId(type, sects) {
  const base = type;
  if (!(sects && sects.some(s => s.id === base))) {
    return base;
  }
  let n = 2;
  while (sects.some(s => s.id === `${base}-${n}`)) {
    n++;
  }
  return `${base}-${n}`;
}

// Shims so all existing code keeps working unchanged
Object.defineProperties(ST, {
  sects: {
    get() {
      return curPage().sects;
    },
    set(v) {
      curPage().sects = v;
    },
    configurable: true,
  },
  shellHtml: {
    get() {
      return curPage().shellHtml;
    },
    set(v) {
      curPage().shellHtml = v;
    },
    configurable: true,
  },
  shellStatus: {
    get() {
      return curPage().shellStatus;
    },
    set(v) {
      curPage().shellStatus = v;
    },
    configurable: true,
  },
  assembled: {
    get() {
      return curPage().assembled;
    },
    set(v) {
      curPage().assembled = v;
    },
    configurable: true,
  },
  editHist: {
    get() {
      return curPage().editHist;
    },
    set(v) {
      curPage().editHist = v;
    },
    configurable: true,
  },
});

// ── LOGO HELPER ──────────────────────────────────────────────────
const hasLogo = () =>
  !!(
    ST.logoImg?.data ||
    (ST.logoUrl?.trim() && /^https?:\/\/.+/i.test(ST.logoUrl.trim())) ||
    (ST.marketingBrief?.logoUrl?.trim() &&
      /^https?:\/\/.+/i.test(ST.marketingBrief.logoUrl.trim())) ||
    ST.existingScrape?.logos?.find(l => l.src)
  );

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export { curPage, hasLogo, ST, sectionId };

window.ST = ST;
